// Shopify caches unversioned module URLs for a year; update this fingerprint when the core changes.
import { createSearchIndex, searchAreaIndex, territoryState } from './plz-exklusiv-core.js?v=975e0f860c59f43e';
import { PlzLeafletMap } from './plz-leaflet-map.js?v=07b461c1e8537755';
import { PlzCart } from './plz-cart.js?v=131f9e29415ea9d9';
import { offerPresentation } from './plz-offer.js?v=b90165d4098b3a07';

class PlzExklusiv extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.previewOnly = this.dataset.previewOnly === 'true';
    this.controller = new AbortController();
    this.mapStates = new Map();
    this.detailChecked = new Set();
    this.pendingProducts = new Map();
    this.byPlz = new Map();
    this.results = [];
    this.activeSuggestion = -1;
    this.requestNumber = 0;
    this.mapCanvas = this.querySelector('[data-map-canvas]');
    this.mapStatus = this.querySelector('[data-map-status]');
    this.root = this.dataset.root || '/';
    this.listen(this.querySelector('[data-search]'), 'input', () => this.suggest());
    this.listen(this.querySelector('[data-search]'), 'keydown', event => this.suggestionKey(event));
    this.listen(this.querySelector('[data-search-form]'), 'submit', event => {
      event.preventDefault();
      this.suggest();
      if (this.results.length === 1 || this.results[0]?.plz === this.querySelector('[data-search]').value.trim()) {
        this.select(this.results[0]);
      }
    });
    this.listen(this.querySelector('[data-checkout]'), 'click', () => this.cart?.checkout());
    this.listen(this.querySelector('[data-contract-form]'), 'submit', event => { event.preventDefault(); this.cart?.checkout(); });
    this.listen(this.querySelector('[data-open-cart]'), 'click', event => {
      const drawer = document.querySelector('cart-drawer-component');
      if (typeof drawer?.open === 'function') {
        event.preventDefault();
        drawer.open();
      }
    });
    this.querySelectorAll('[data-example-plz]').forEach(button => this.listen(button, 'click', () => this.select(this.byPlz.get(button.dataset.examplePlz))));
    this.querySelectorAll('[data-zoom]').forEach(button => this.listen(button, 'click', () => this.mapRenderer?.zoom(button.dataset.zoom)));
    this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.cart?.destroy();
    this.mapRenderer?.destroy();
    this.mapRenderer = null;
    this.started = false;
  }

  listen(element, type, handler, options = {}) {
    element.addEventListener(type, handler, {...options, signal: this.controller.signal});
  }

  async json(url, options = {}) {
    const response = await fetch(url, {cache: 'no-store', credentials: 'same-origin', signal: this.controller.signal, ...options});
    if (!response.ok) throw new Error('Die Anfrage konnte nicht abgeschlossen werden. Bitte erneut prüfen.');
    return response.json();
  }

  async load() {
    try {
      this.rows = await this.json(this.dataset.index, {cache: 'force-cache'});
      this.searchIndex = createSearchIndex(this.rows);
      this.byPlz = new Map(this.rows.map(row => [row.plz, row]));
      if (!this.previewOnly) {
        this.cart = new PlzCart(this, {root: this.root, rows: this.byPlz, money: cents => this.money(cents),
          getProduct: area => this.product(area),
          onChange: () => { if (this.current && !this.loadingDetail) this.renderResult(this.current, this.currentData); }
        });
        this.cart.load();
      }
      this.querySelector('[data-search-status]').textContent = 'Suche in 8.170 PLZ-Gebieten.';
      this.suggest();
      const initial = new URL(location.href).searchParams.get('plz') || this.dataset.initialPlz;
      if (!this.querySelector('[data-search]').value.trim() && this.byPlz.has(initial)) this.select(this.byPlz.get(initial));
      this.loadMap();
      if (!this.previewOnly) this.loadMapStates();
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.querySelector('[data-search-status]').textContent = 'PLZ-Daten konnten nicht geladen werden. Bitte Seite neu laden.';
      this.querySelector('[data-map-status]').textContent = 'Karte derzeit nicht verfügbar.';
    }
  }

  suggest() {
    const input = this.querySelector('[data-search]');
    const results = this.searchIndex ? searchAreaIndex(this.searchIndex, input.value) : [];
    const changed = results.length !== this.results.length || results.some((area, index) => area !== this.results[index]);
    this.results = results;
    this.activeSuggestion = -1;
    input.removeAttribute('aria-activedescendant');
    const list = this.querySelector('[data-suggestions]');
    if (changed) {
      const fragment = document.createDocumentFragment();
      for (const [index, area] of this.results.entries()) {
        const item = document.createElement('li');
        item.id = `${list.id}-${index}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.textContent = `${area.plz} · ${area.places.replaceAll(';', ',')}`;
        item.addEventListener('click', () => this.select(area));
        fragment.append(item);
      }
      list.replaceChildren(fragment);
    } else {
      list.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    }
    if (list.hidden !== !results.length) list.hidden = !results.length;
    input.setAttribute('aria-expanded', String(!list.hidden));
    const status = this.querySelector('[data-search-status]');
    const message = input.value.trim() ? this.results.length
      ? `${this.results.length === 20 ? 'Bis zu ' : ''}${this.results.length} Treffer. Bitte PLZ auswählen.`
      : 'Keine passende PLZ gefunden. Bitte Eingabe prüfen.' : 'Suche in 8.170 PLZ-Gebieten.';
    if (status.textContent !== message) status.textContent = message;
  }

  suggestionKey(event) {
    const input = event.currentTarget;
    const list = this.querySelector('[data-suggestions]');
    if (event.key === 'Escape') {
      event.preventDefault();
      list.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      return;
    }
    if (event.key === 'Enter' && this.activeSuggestion >= 0 && !list.hidden) {
      event.preventDefault();
      this.select(this.results[this.activeSuggestion]);
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !this.results.length) return;
    event.preventDefault();
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    this.activeSuggestion = (this.activeSuggestion + (event.key === 'ArrowDown' ? 1 : -1) + this.results.length) % this.results.length;
    [...list.children].forEach((item, index) => item.setAttribute('aria-selected', String(index === this.activeSuggestion)));
    const active = list.children[this.activeSuggestion];
    input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({block: 'nearest'});
  }

  async product(area) {
    if (this.previewOnly) return null;
    // Share only in-flight reads. There is no cached availability between cart operations.
    // A concurrent cart refresh must not cancel the user's selected territory request.
    if (this.pendingProducts.has(area.plz)) return this.pendingProducts.get(area.plz);
    const controller = this.controller;
    const pending = this.json(`${this.root}products/${encodeURIComponent(area.handle)}?view=plz-data&_plz=${Date.now()}`, {signal: controller.signal})
      .then(data => {
        if (this.controller !== controller || !this.isConnected) throw new DOMException('Abgebrochen', 'AbortError');
        this.updateMapState(area, data);
        if (this.current?.plz === area.plz) this.currentData = data;
        return data;
      }).catch(error => {
        if (error.name !== 'AbortError' && this.controller === controller && this.isConnected) {
          this.updateMapState(area, null);
          if (this.current?.plz === area.plz) this.currentData = null;
        }
        throw error;
      }).finally(() => { if (this.pendingProducts.get(area.plz) === pending) this.pendingProducts.delete(area.plz); });
    this.pendingProducts.set(area.plz, pending);
    return pending;
  }

  node(tag, text, className) {
    const element = document.createElement(tag);
    if (text !== undefined) element.textContent = text;
    if (className) element.className = className;
    return element;
  }

  money(cents) {
    return new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(cents / 100);
  }

  async select(area) {
    if (!area) return;
    this.current = area;
    this.currentData = null;
    this.loadingDetail = true;
    this.mapRenderer?.select(area.plz);
    const request = ++this.requestNumber;
    const input = this.querySelector('[data-search]');
    input.value = area.plz;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    this.querySelector('[data-suggestions]').hidden = true;
    this.querySelector('[data-result]').replaceChildren(this.node('p', 'Verfügbarkeit wird geprüft …'));
    if (area.review) {
      this.loadingDetail = false;
      this.renderResult(area, null);
      return;
    }
    try {
      const data = await this.product(area);
      if (request !== this.requestNumber || !this.isConnected) return;
      this.loadingDetail = false;
      this.currentData = data;
      this.renderResult(area, data);
    } catch (error) {
      if (error.name !== 'AbortError' && request === this.requestNumber && this.isConnected) {
        this.loadingDetail = false;
        this.renderResult(area, null);
      }
    }
  }

  renderResult(area, data) {
    const box = this.querySelector('[data-result]');
    if (!data) this.updateMapState(area, null);
    const state = offerPresentation(area, data, {previous: this.mapStates.get(area.plz), earlyBird: this.dataset.earlyBird === 'true'});
    box.dataset.state = state.status;
    const header = this.node('div', undefined, 'plz-offer-header');
    const badge = this.node('span', state.badge, 'plz-offer-status');
    badge.dataset.state = state.status;
    header.append(badge, this.node('span', `Kategorie ${area.category}`, 'plz-offer-category'));
    const places = area.places.split(';').map(place => place.trim());
    const title = this.node('h3', `PLZ ${area.plz}`, 'plz-offer-title');
    title.append(this.node('span', places[0]));
    const location = this.node('p', places.length > 1 ? places.slice(1).join(' · ') : 'Dein exklusives Provisionsgebiet', 'plz-offer-location');
    const prices = this.node('div', undefined, 'plz-offer-prices');
    const selector = document.createElement('select');
    selector.setAttribute('aria-label', 'Jahresabo auswählen');
    const validData = data?.license === true && data.plz === area.plz;
    for (const plan of validData ? data.plans || [] : []) selector.add(new Option(plan.name, String(plan.id)));
    const selectedPlan = () => validData ? data.plans?.find(plan => String(plan.id) === selector.value) : null;
    const displayPrice = () => {
      const offer = offerPresentation(area, data, {previous: this.mapStates.get(area.plz), earlyBird: this.dataset.earlyBird === 'true', plan: selectedPlan()});
      prices.replaceChildren();
      if (offer.compareAt) prices.append(this.node('del', this.priceLabel(offer.compareAt)));
      prices.append(this.node('strong', this.priceLabel(offer.price)));
      prices.append(this.node('small', `${offer.compareAt ? 'Early Bird · ' : ''}pro Jahr · inkl. USt.`));
      if (offer.priceIsGuide) prices.append(this.node('small', 'Kategoriepreis · Kaufbarkeit wird separat geprüft', 'plz-price-guide'));
    };
    selector.addEventListener('change', displayPrice);
    displayPrice();
    const facts = this.node('dl', undefined, 'plz-offer-facts');
    const population = this.node('div');
    population.append(this.node('dt', 'Einwohner im PLZ-Gebiet'),
      this.node('dd', area.review ? 'In Prüfung' : `ca. ${area.population.toLocaleString('de-DE')}`), this.node('small', 'Zensus 2022'));
    const territory = this.node('div');
    territory.append(this.node('dt', 'Gebiet'), this.node('dd', area.plz), this.node('small', 'Exklusive PLZ-Lizenz'));
    facts.append(population, territory);
    const availability = this.node('div', undefined, 'plz-offer-availability');
    availability.dataset.state = state.status;
    const message = this.node('div');
    message.append(this.node('strong', state.title), this.node('p', state.detail));
    const icon = this.node('span', state.icon);
    icon.setAttribute('aria-hidden', 'true');
    availability.append(icon, message);
    const actions = this.node('div', undefined, 'plz-offer-actions');
    const inCart = this.cart?.has(area.plz);
    const add = this.node('button', this.previewOnly ? 'Verkauf pausiert' : inCart ? 'Im Warenkorb' : state.button, 'plz-button');
    add.type = 'button';
    add.disabled = this.previewOnly || !state.canBuy || !this.cart || this.cart.busy || inCart;
    add.addEventListener('click', () => this.cart?.add(area, data, selectedPlan()));
    actions.append(add);
    if (!this.previewOnly && (state.canBuy || inCart)) {
      const direct = this.node('button', 'Direkt zur Kasse', 'plz-button plz-button--secondary');
      direct.type = 'button';
      direct.disabled = !this.cart || this.cart.busy;
      direct.addEventListener('click', () => inCart ? this.cart?.checkout() : this.cart?.add(area, data, selectedPlan(), {checkout: true}));
      actions.append(direct);
    }
    box.replaceChildren(header, title, location, prices);
    if (validData && data.plans?.length > 1) box.append(selector);
    box.append(facts, availability, actions,
      this.node('p', 'Ausschließlich Einwohner innerhalb der gewählten PLZ-Grenze. Keine Stadt- oder Gemeindesumme.', 'plz-offer-footnote'));
    const retry = this.node('button', 'Verfügbarkeit erneut prüfen', 'plz-offer-retry');
    retry.type = 'button';
    retry.addEventListener('click', () => this.select(area));
    box.append(retry);
  }

  priceLabel(cents) {
    return new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR', minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2}).format(cents / 100);
  }

  updateMapState(area, data) {
    // Even a failed detail check is newer than a pending background snapshot.
    this.detailChecked.add(area.plz);
    const state = territoryState(area, data, this.mapStates.get(area.plz));
    this.mapStates.set(area.plz, state);
    this.mapRenderer?.setState(area.plz, state);
  }

  async loadMapStates() {
    if (!this.dataset.mapStates) return;
    const first = new URL(this.dataset.mapStates, location.origin);
    let next = first;
    const seen = new Set();
    try {
      // Shopify exposes at most 250 products per page; fetch sequentially in the background.
      while (next && seen.size < 40 && !this.controller.signal.aborted) {
        next.searchParams.set('view', 'plz-map-state');
        if (next.origin !== location.origin || next.pathname !== first.pathname || seen.has(next.href)) break;
        seen.add(next.href);
        const snapshot = await this.json(next.href);
        if (!Array.isArray(snapshot.entries)) break;
        for (const entry of snapshot.entries) {
          const area = this.byPlz.get(entry.plz);
          if (!area || this.detailChecked.has(entry.plz) || !['assigned', 'blocked', 'unassigned'].includes(entry.state)) continue;
          const state = territoryState(area, {
            license: true, plz: area.plz, mapState: entry.state, saleAvailable: entry.saleAvailable
          });
          this.mapStates.set(area.plz, state);
          this.mapRenderer?.setState(area.plz, state);
        }
        next = snapshot.next ? new URL(snapshot.next, next) : null;
      }
    } catch (error) {
      // Draft or unpublished collections have no public inventory snapshot. Selection still checks live.
      if (error.name !== 'AbortError') this.mapStatus.dataset.inventory = 'unavailable';
    }
  }

  async loadMap() {
    const renderer = new PlzLeafletMap(this.mapCanvas, {
      rows: this.byPlz, initialPlz: this.current?.plz, onSelect: area => this.select(area)
    });
    this.mapRenderer = renderer;
    for (const [plz, state] of this.mapStates) renderer.setState(plz, state);
    try {
      const result = await renderer.load(this.dataset.map, this.controller.signal);
      if (!this.isConnected || this.mapRenderer !== renderer) return;
      this.mapStatus.textContent = 'Gebiet berühren oder anklicken · Karte ziehen';
      if (result.missing.length) this.mapStatus.textContent += ` · ${result.missing.join(', ')}: bitte Suche verwenden`;
    } catch (error) {
      if (error.name !== 'AbortError') this.mapStatus.textContent = 'Die Karte ist derzeit nicht verfügbar. Bitte die PLZ-Suche verwenden.';
    }
  }
}

if (!customElements.get('plz-exklusiv')) customElements.define('plz-exklusiv', PlzExklusiv);
